/**
 * useSwipeGesture Hook
 * Modern swipe gesture detection for mobile interfaces
 * Uses Pointer Events API for best cross-platform support
 */

import { useRef, useCallback, useEffect } from 'react';

const SWIPE_THRESHOLD = 100; // minimum distance for swipe (increased from 50)
const SWIPE_VELOCITY_THRESHOLD = 0.5; // minimum velocity (increased from 0.3)
const MAX_VERTICAL_DEVIATION = 80; // max vertical movement for horizontal swipe (decreased from 100)
const EDGE_SWIPE_ZONE = 80; // pixels from edge where swipe can start

export function useSwipeGesture({
  onSwipeLeft,
  onSwipeRight,
  onSwipeUp,
  onSwipeDown,
  threshold = SWIPE_THRESHOLD,
  velocityThreshold = SWIPE_VELOCITY_THRESHOLD,
  enabled = true,
  preventScroll = false,
  edgeOnly = false, // only allow swipes from screen edges
}) {
  const touchStart = useRef(null);
  const touchEnd = useRef(null);
  const touchTime = useRef(null);

  const handleTouchStart = useCallback((e) => {
    if (!enabled) return;

    const touch = e.touches?.[0] || e;
    const startX = touch.clientX;
    const screenWidth = window.innerWidth;

    // If edgeOnly is enabled, only allow swipes starting from edges
    if (edgeOnly) {
      const isFromLeftEdge = startX < EDGE_SWIPE_ZONE;
      const isFromRightEdge = startX > screenWidth - EDGE_SWIPE_ZONE;

      if (!isFromLeftEdge && !isFromRightEdge) {
        touchStart.current = null;
        return;
      }
    }

    touchStart.current = {
      x: startX,
      y: touch.clientY,
    };
    touchTime.current = Date.now();
    touchEnd.current = null;
  }, [enabled, edgeOnly]);

  const handleTouchMove = useCallback((e) => {
    if (!enabled || !touchStart.current) return;

    const touch = e.touches?.[0] || e;
    touchEnd.current = {
      x: touch.clientX,
      y: touch.clientY,
    };

    // Prevent scroll if needed
    if (preventScroll) {
      const deltaX = Math.abs(touchEnd.current.x - touchStart.current.x);
      const deltaY = Math.abs(touchEnd.current.y - touchStart.current.y);

      // If horizontal swipe is dominant, prevent scroll
      if (deltaX > deltaY && deltaX > 10) {
        e.preventDefault();
      }
    }
  }, [enabled, preventScroll]);

  const handleTouchEnd = useCallback(() => {
    if (!enabled || !touchStart.current || !touchEnd.current) return;

    const deltaX = touchEnd.current.x - touchStart.current.x;
    const deltaY = touchEnd.current.y - touchStart.current.y;
    const deltaTime = Date.now() - touchTime.current;

    const velocityX = Math.abs(deltaX) / deltaTime;
    const velocityY = Math.abs(deltaY) / deltaTime;

    // Determine if swipe is valid
    const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
    const isVerticalSwipe = Math.abs(deltaY) > Math.abs(deltaX);

    if (isHorizontalSwipe && Math.abs(deltaX) > threshold && velocityX > velocityThreshold) {
      if (Math.abs(deltaY) < MAX_VERTICAL_DEVIATION) {
        if (deltaX > 0) {
          onSwipeRight?.();
        } else {
          onSwipeLeft?.();
        }
      }
    } else if (isVerticalSwipe && Math.abs(deltaY) > threshold && velocityY > velocityThreshold) {
      if (deltaY > 0) {
        onSwipeDown?.();
      } else {
        onSwipeUp?.();
      }
    }

    // Reset
    touchStart.current = null;
    touchEnd.current = null;
    touchTime.current = null;
  }, [enabled, threshold, velocityThreshold, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown]);

  return {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    // Also support pointer events for broader compatibility
    onPointerDown: handleTouchStart,
    onPointerMove: handleTouchMove,
    onPointerUp: handleTouchEnd,
  };
}

/**
 * useTabSwipe Hook
 * Specialized hook for swiping between tabs
 */
export function useTabSwipe({ currentTab, totalTabs, onTabChange, enabled = true, edgeOnly = false }) {
  const swipeHandlers = useSwipeGesture({
    onSwipeLeft: () => {
      if (currentTab < totalTabs - 1) {
        onTabChange(currentTab + 1);
      }
    },
    onSwipeRight: () => {
      if (currentTab > 0) {
        onTabChange(currentTab - 1);
      }
    },
    enabled,
    preventScroll: true,
    edgeOnly,
  });

  return swipeHandlers;
}

/**
 * useSwipeToAction Hook
 * For swipe-to-reveal actions (like delete, archive)
 */
export function useSwipeToAction({
  onRevealLeft,
  onRevealRight,
  onConfirmLeft,
  onConfirmRight,
  threshold = 80,
  confirmThreshold = 150,
  enabled = true,
}) {
  const containerRef = useRef(null);
  const offsetRef = useRef(0);
  const isDraggingRef = useRef(false);
  const touchStart = useRef(null);

  const handleTouchStart = useCallback((e) => {
    if (!enabled) return;
    const touch = e.touches[0];
    touchStart.current = { x: touch.clientX, y: touch.clientY };
    isDraggingRef.current = true;
  }, [enabled]);

  const handleTouchMove = useCallback((e) => {
    if (!enabled || !isDraggingRef.current || !containerRef.current) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - (touchStart.current?.x || touch.clientX);

    offsetRef.current = deltaX;
    containerRef.current.style.transform = `translateX(${deltaX}px)`;

    // Reveal actions
    if (Math.abs(deltaX) > threshold) {
      if (deltaX > 0) {
        onRevealRight?.();
      } else {
        onRevealLeft?.();
      }
    }
  }, [enabled, threshold, onRevealLeft, onRevealRight]);

  const handleTouchEnd = useCallback(() => {
    if (!enabled || !containerRef.current) return;

    const offset = offsetRef.current;

    // Check for confirm threshold
    if (Math.abs(offset) > confirmThreshold) {
      if (offset > 0) {
        onConfirmRight?.();
      } else {
        onConfirmLeft?.();
      }
    }

    // Reset
    containerRef.current.style.transform = 'translateX(0)';
    offsetRef.current = 0;
    isDraggingRef.current = false;
  }, [enabled, confirmThreshold, onConfirmLeft, onConfirmRight]);

  return {
    ref: containerRef,
    handlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
  };
}

export default useSwipeGesture;
